'use client'

import { useState, Suspense } from 'react'
import { HelpNavButton } from '@/components/ui/help-nav-button'
import { CategorySidebar } from './CategorySidebar'
import { ActiveCategoryProvider } from '@/lib/blog/active-category-context'
import type { CategoryWithCountNode, TagWithTranslation } from '@/lib/blog/article-types'

interface BlogLayoutProps {
  locale: string
  categoryTree: CategoryWithCountNode[]
  tags: TagWithTranslation[]
  children: React.ReactNode
}

const strings: Record<string, { subtitle: string; backButton: string; mobileTitle: string }> = {
  en: { subtitle: 'Blog', backButton: '← Back to app', mobileTitle: 'Chatbotty - Blog' },
  cs: { subtitle: 'Blog', backButton: '← Zpět do aplikace', mobileTitle: 'Chatbotty - Blog' },
  de: { subtitle: 'Blog', backButton: '← Zurück zur App', mobileTitle: 'Chatbotty - Blog' },
  fr: { subtitle: 'Blog', backButton: '← Retour à l\'app', mobileTitle: 'Chatbotty - Blog' },
}

function getStrings(locale: string) {
  return strings[locale] ?? strings['en']
}

export function BlogLayout({ locale, categoryTree, tags, children }: BlogLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const s = getStrings(locale)

  return (
    <ActiveCategoryProvider>
    <div className="min-h-screen bg-background text-foreground">
      {/* Fixed Sidebar */}
      <nav className={`fixed left-0 top-0 z-50 h-full w-64 transform border-r border-border/70 bg-card/95 shadow-2xl shadow-black/10 backdrop-blur transition-transform duration-300 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
        {/* Logo */}
        <div className="border-b border-border/70 p-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
              <img src="/chatbotty-logo.svg" className="h-7 w-7" alt="Chatbotty logo" />
            </div>
            <div>
              <h2 className="font-bold text-foreground">Chatbotty</h2>
              <p className="text-xs text-muted-foreground">{s.subtitle}</p>
            </div>
          </div>
        </div>

        {/* Category Navigation */}
        <div className="p-4 overflow-y-auto h-[calc(100vh-180px)]">
          <Suspense fallback={null}>
            <CategorySidebar categories={categoryTree} tags={tags} locale={locale} />
          </Suspense>
        </div>

        {/* Back button */}
        <div className="absolute bottom-4 left-4 right-4">
          <HelpNavButton
            className="flex items-center justify-center w-full px-4 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl font-medium hover:from-indigo-600 hover:to-purple-700 transition-all duration-200 shadow-lg text-sm"
            fallbackHref="/"
          >
            {s.backButton}
          </HelpNavButton>
        </div>
      </nav>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile Header */}
      <header className="flex w-full items-center justify-between border-b border-border/70 bg-card/95 px-3 py-3 shadow-sm backdrop-blur lg:hidden">
        <div className="flex items-center space-x-2 min-w-0 flex-1">
          <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <img src="/chatbotty-logo.svg" className="h-6 w-6" alt="Chatbotty logo" />
          </div>
          <h1 className="truncate text-sm font-semibold text-foreground">{s.mobileTitle}</h1>
        </div>
        <button
          className="flex-shrink-0 p-2 text-muted-foreground transition-colors hover:text-foreground"
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          {sidebarOpen ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="6" x2="21" y2="6"/>
              <line x1="3" y1="12" x2="21" y2="12"/>
              <line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
          )}
        </button>
      </header>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen">
        <div className="max-w-7xl mx-auto px-6 py-8 lg:px-8">
          {children}
        </div>
      </main>
    </div>
    </ActiveCategoryProvider>
  )
}
